"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import * as React from "react";

interface ScrollProps extends React.HTMLAttributes<HTMLAnchorElement> {
	to: string;
	containerId?: string;
	container?: object;
	activeClass?: string;
	activeStyle?: object;
	spy?: boolean;
	horizontal?: boolean;
	smooth?: boolean | string;
	offset?: number;
	delay?: number;
	isDynamic?: boolean;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
	duration?: number | (() => void);
	absolute?: boolean;
	onSetActive?: () => void;
	onSetInactive?: () => void;
	ignoreCancelEvents?: boolean;
	hashSpy?: boolean;
	saveHashHistory?: boolean;
	spyThrottle?: number;
	block?: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
}

const Scroll = (props: ScrollProps) => {
	const {
		to,
		containerId,
		container,
		activeClass,
		activeStyle,
		spy,
		horizontal,
		smooth,
		offset,
		delay,
		isDynamic,
		onClick,
		duration,
		absolute,
		onSetActive,
		onSetInactive,
		ignoreCancelEvents,
		hashSpy,
		saveHashHistory,
		spyThrottle,
		block = "start",
		inline = "start",
		children,
		className,
	} = props;

	const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		if (onClick) {
			onClick(event);
		}

		const element = document.getElementById(to);

		if (!element) {
			return;
		}

		element.style.scrollMarginTop = "7rem";
		element.scrollIntoView({
			behavior: "smooth",
			block: block,
			inline: inline,
		});
	};

	return (
		<Link href={`#${to}`} className={className} onClick={(e) => handleClick(e)}>
			{children}
		</Link>
	);
};

const ScrollToHash = () => {
	const pathname = usePathname();
	const [isLoaded, setIsLoaded] = React.useState(false);

	React.useEffect(() => {
		const handleLoad = () => {
			setIsLoaded(true);
		};

		if (document.readyState === "complete") {
			handleLoad();
		} else {
			window.addEventListener("load", handleLoad);
			return () => window.removeEventListener("load", handleLoad);
		}
	}, []);

	React.useEffect(() => {
		if (!isLoaded) return;

		const scrollToHash = () => {
			if (typeof window !== "undefined") {
				const hash = window.location.hash;

				if (hash) {
					const target = document.querySelector(hash);
					if (target) {
						setTimeout(() => {
							target.scrollIntoView({ behavior: "smooth" });
						}, 1000); // Delay to allow rendering
					}
				}
			}
		};

		scrollToHash();
	}, [pathname, isLoaded]);

	return null;
};

export { Scroll, ScrollToHash };
